'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useId } from 'utils';
import { focusWithoutScrolling } from '@react-aria/utils';
import { useControlledComponent } from '../../hooks/useControlledComponent';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import type { TargetClickBindingProps } from './useClickTarget';
import { useClickTarget } from './useClickTarget';

export type TargetClickProps = {
  ref: any;
} & TargetClickBindingProps &
  Pick<React.HTMLAttributes<HTMLElement>, 'onContextMenu' | 'id'>;

export type TargetBindingProps = TargetClickProps;

export type UsePopoverProps = {
  /** Whether or not the Popover is showing. */
  isShowing?: boolean;
  /** Handle changing whether the Popover is showing (when used as a controlled component). */
  onRequestChange?: (isShowing: boolean) => void;
  /** Determine if the Popover should appear by default when using as an uncontrolled component. */
  defaultShowing?: boolean;
  /** A reference to an element which should receive focus on mount. */
  initialFocusRef?: React.MutableRefObject<HTMLElement>;
  /**
   * Whether or not the Popover should refocus on an element when it closes
   */
  hasRefocusEventOnClose?: boolean;
  /** Whether the escape key closes the Popover. */
  shouldCloseOnEscape?: boolean;
  /** Whether the Popover should focus on mount. */
  shouldFocusOnMount?: boolean;
  /** An optional supplied target element */
  targetEl?: any;
  /** Optional aria-label. Otherwise Popover will be labelled by trigger element. */
  ariaLabel?: string;
};

export function usePopover({
  targetEl: providedTargetEl,
  isShowing: isShowingProp,
  onRequestChange,
  defaultShowing,
  initialFocusRef,
  hasRefocusEventOnClose = true,
  shouldFocusOnMount = true,
  shouldCloseOnEscape = true,
  ariaLabel,
}: UsePopoverProps = {}) {
  const id = useId();
  const buttonId = useId();

  // track refs
  const [targetEl, setTargetEl] = useState(providedTargetEl);
  const triggerRef = useRef(null);

  const setTargetRef = useCallback(
    (el: any) => {
      triggerRef.current = el;
      if (typeof providedTargetEl === 'undefined') {
        setTargetEl(el);
      }
    },
    [providedTargetEl]
  );

  const popoverRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (providedTargetEl) {
      setTargetEl(providedTargetEl);
    }
  }, [setTargetEl, providedTargetEl]);

  // popover can be uncontrolled or controlled. This abstraction
  // handles both
  const [isShowing, setIsShowing] = useControlledComponent(
    isShowingProp,
    onRequestChange,
    defaultShowing
  );

  /**
   * Trigger showing a popover
   */

  const showPopover = useCallback(() => {
    setIsShowing(true);
  }, [setIsShowing]);

  /**
   * Trigger hiding a popover
   */

  const hidePopover = useCallback(() => {
    setIsShowing(false);

    if (!hasRefocusEventOnClose) {
      return;
    }

    // If the actively focused element is not contained within the popover, but has
    // instead been placed elsewhere (like in a Dialog) then ignore the refocus event,
    // and assume that the consumer will handle focus.
    if (!popoverRef.current?.contains(document.activeElement)) {
      return;
    }

    const focusElement = triggerRef.current || targetEl;

    if (focusElement && focusElement.focus) {
      focusWithoutScrolling(focusElement);
    }
  }, [hasRefocusEventOnClose, setIsShowing, targetEl]);

  /**
   * Get required click target binding props.
   */

  const targetBindings = useClickTarget({
    id,
    // @ts-expect-error FIXME(strictNullChecks)
    isShowing,
    show: showPopover,
    hide: hidePopover,
  });

  /**
   * Close a popover when clicking outside of it if
   * in "click" mode.
   */

  const { bind: bindOnClickOutside } = useOnClickOutside(hidePopover, {
    enabled: isShowing,
  });

  /**
   * Handle focus when showing
   */

  useEffect(() => {
    // if rendering the popover, focus the specified element
    if (shouldFocusOnMount && isShowing) {
      window.requestAnimationFrame(() => {
        if (initialFocusRef && initialFocusRef.current) {
          initialFocusRef.current.focus();
        } else if (popoverRef.current) {
          popoverRef.current.focus();
        }
      });
    }
  }, [shouldFocusOnMount, initialFocusRef, isShowing, popoverRef]);

  /**
   * Potentially close the popover and return focus
   * back to the target when escape is pressed.
   */

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (shouldCloseOnEscape && e.key === 'Escape') {
        e.stopPropagation();
        hidePopover();
      }
    },
    [shouldCloseOnEscape, hidePopover]
  );

  const targetProps: TargetBindingProps = {
    ref: setTargetRef,
    ...bindOnClickOutside,
    ...targetBindings,
    id: buttonId,
    onContextMenu: (e) => {
      e.preventDefault();
      e.stopPropagation();
      showPopover();
    },
  };

  const contentProps = {
    isShowing,
    target: targetEl,
    shouldUsePositioner: true,
    id,
    role: 'dialog',
    'aria-modal': false,
    'aria-labelledby': ariaLabel ? undefined : buttonId,
    'aria-label': ariaLabel,
    ref: popoverRef,
    onKeyDown: handleKeyDown,
    ...bindOnClickOutside,
  };

  return {
    id,
    hidePopover,
    showPopover,
    isShowing,
    targetEl,
    bindContent: contentProps,
    bindTarget: targetProps,
  };
}
